<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useCsmlResizable } from '../../composables/useCsmlResizable';
import Button from 'dashboard/components-next/button/Button.vue';
import { getNodeIssues } from '../../composables/useCsmlNodeMeta';
import { nodeTemplates } from '../../constants/csmlNodeTemplates';
import { getCategoryStyle } from '../../constants/csmlNodeCategories';
import {
  CALENDAR_MODES,
  INPUT_TYPES,
  OAUTH_PROVIDERS,
} from '../../constants/csmlFieldOptions';
import CsmlPropertySection from './CsmlPropertySection.vue';
import CsmlOptionListEditor from './properties/CsmlOptionListEditor.vue';
import CsmlImageUpload from './properties/CsmlImageUpload.vue';
import CsmlFileUpload from './properties/CsmlFileUpload.vue';
import CsmlCarouselEditor from './properties/CsmlCarouselEditor.vue';
import CsmlApiEditor from './properties/CsmlApiEditor.vue';
import CsmlAssignEditor from './properties/CsmlAssignEditor.vue';
import CsmlWorkingHoursEditor from './properties/CsmlWorkingHoursEditor.vue';

const props = defineProps({
  node: { type: Object, required: true },
});

const emit = defineEmits(['update', 'close', 'duplicate', 'delete']);

const { width, startResize } = useCsmlResizable('csml-builder-panel-width', {
  min: 280,
  max: 480,
  initial: 320,
});

const { t, te } = useI18n();

const CHOICE_TYPES = ['radio', 'multiselect', 'checkbox', 'dropdown'];
const MEDIA_TYPES = ['file', 'video', 'audio'];

const type = computed(() => props.node?.type || '');
const isChoice = computed(() => CHOICE_TYPES.includes(type.value));
const isButtonList = computed(() =>
  ['question', 'quickreply'].includes(type.value)
);
const isMultiSelectLike = computed(() =>
  ['multiselect', 'checkbox'].includes(type.value)
);

const style = computed(() => getCategoryStyle(type.value));
const icon = computed(() => nodeTemplates[type.value]?.icon || 'i-lucide-box');
const typeLabel = computed(() => {
  const key = `AGENT_BOTS.VISUAL_BUILDER.NODES.${type.value.toUpperCase()}.LABEL`;
  return te(key) ? t(key) : props.node?.label;
});

const issues = computed(() => getNodeIssues(props.node));
const isStart = computed(() => type.value === 'start');

// Which sections have anything to show for this node type.
const hasButtons = computed(
  () =>
    isButtonList.value ||
    isChoice.value ||
    type.value === 'router' ||
    (isStart.value && !!props.node?.buttons?.length)
);
const hasAdvanced = computed(
  () =>
    ['url', 'latex', 'question', 'input', 'calendar', 'signature'].includes(
      type.value
    ) ||
    isChoice.value ||
    (isStart.value && !!props.node?.buttons?.length)
);

// A node's prompt text is labelled differently depending on what the node
// does, so the one Content field does not read as "message" on a form.
const contentLabel = computed(() => {
  if (isChoice.value || isButtonList.value) {
    return t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.PROMPT');
  }
  return t('AGENT_BOTS.VISUAL_BUILDER.CONTENT');
});

const selectedOptions = computed(() => [
  { value: '', label: t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.NONE') },
  ...(props.node?.options || []).map(option => ({
    value: option.payload,
    label: option.label,
  })),
]);

const patch = patchObj => {
  if (!props.node) return;
  emit('update', { id: props.node.id, patch: patchObj });
};

// Upload completion must use the id captured when the request started.
// patch() reads the node selected now, which may already be a different one.
const onMediaUploaded = ({ nodeId, url }) => {
  if (nodeId == null) return;
  emit('update', { id: nodeId, patch: { content: url } });
};

const patchNumber = (field, value) => patch({ [field]: Number(value) || 0 });

const keywords = () => props.node?.keywords || [];

const updateKeyword = (index, match) => {
  patch({
    keywords: keywords().map((rule, i) =>
      i === index ? { ...rule, match } : rule
    ),
  });
};

const removeKeyword = index => {
  patch({ keywords: keywords().filter((_, i) => i !== index) });
};

const addKeyword = () => {
  patch({ keywords: [...keywords(), { match: '' }] });
};
</script>

<template>
  <aside
    class="relative flex flex-col max-h-full overflow-hidden border shadow-lg pointer-events-auto rounded-xl border-n-weak bg-n-solid-1/95 backdrop-blur"
    :style="{ width: `${width}px` }"
  >
    <div
      class="absolute inset-y-0 z-10 w-1.5 cursor-col-resize start-0 hover:bg-n-brand/30"
      @mousedown="startResize"
    />
    <header
      class="flex items-center gap-2.5 px-3 py-2.5 border-b shrink-0 border-n-weak"
    >
      <span
        class="flex items-center justify-center rounded-lg size-7 shrink-0"
        :class="style.chip"
      >
        <Icon :icon="icon" class="size-3.5" />
      </span>
      <div class="flex-1 min-w-0">
        <h3 class="text-sm font-semibold truncate text-n-slate-12">
          {{ typeLabel }}
        </h3>
        <p class="text-[11px] text-n-slate-11">
          {{ t('AGENT_BOTS.VISUAL_BUILDER.NODE_ID', { id: node.id }) }}
        </p>
      </div>
      <Button
        v-if="!isStart"
        size="sm"
        variant="ghost"
        color="ruby"
        icon="i-lucide-trash-2"
        is-icon-only
        :title="t('AGENT_BOTS.VISUAL_BUILDER.DELETE_NODE')"
        @click="emit('delete', node.id)"
      />
      <Button
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-x"
        is-icon-only
        @click="emit('close')"
      />
    </header>

    <div
      v-if="issues.length"
      class="flex flex-col gap-1 px-3 py-2 border-b shrink-0 border-n-ruby-6 bg-n-ruby-3"
    >
      <p
        v-for="issue in issues"
        :key="issue"
        class="flex items-start gap-1.5 text-xs text-n-ruby-11"
      >
        <Icon icon="i-lucide-triangle-alert" class="mt-px size-3 shrink-0" />
        {{ t(`AGENT_BOTS.VISUAL_BUILDER.ISSUES.${issue}`) }}
      </p>
    </div>

    <div class="flex flex-col flex-1 min-h-0 overflow-y-auto">
      <CsmlPropertySection
        :title="t('AGENT_BOTS.VISUAL_BUILDER.SECTIONS.CONTENT')"
      >
        <TextArea
          v-if="['start', 'say'].includes(type)"
          :model-value="node.content"
          :label="contentLabel"
          @update:model-value="patch({ content: $event })"
        />

        <p v-if="type === 'hold'" class="text-xs text-n-slate-11">
          {{ t('AGENT_BOTS.VISUAL_BUILDER.HOLD_HINT') }}
        </p>

        <Input
          v-if="type === 'typing'"
          type="number"
          size="sm"
          :model-value="node.duration"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.DURATION')"
          @update:model-value="
            patch({ duration: Number($event) || 0, content: String($event) })
          "
        />

        <CsmlImageUpload
          v-if="type === 'image'"
          :node-id="node.id"
          :model-value="node.content"
          @update:model-value="patch({ content: $event })"
          @uploaded="onMediaUploaded"
        />

        <CsmlFileUpload
          v-if="MEDIA_TYPES.includes(type)"
          :node-id="node.id"
          :kind="type"
          :model-value="node.content"
          @update:model-value="patch({ content: $event })"
          @uploaded="onMediaUploaded"
        />

        <template v-if="type === 'url'">
          <Input
            :model-value="node.content"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.URL')"
            @update:model-value="patch({ content: $event })"
          />
          <Input
            :model-value="node.urlText"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.LINK_TEXT')"
            @update:model-value="patch({ urlText: $event })"
          />
        </template>

        <TextArea
          v-if="type === 'latex'"
          :model-value="node.content"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.LATEX')"
          @update:model-value="patch({ content: $event })"
        />

        <TextArea
          v-if="isButtonList || isChoice"
          :model-value="node.content"
          :label="contentLabel"
          @update:model-value="patch({ content: $event })"
        />

        <Input
          v-if="['input', 'calendar', 'signature'].includes(type)"
          :model-value="node.content"
          size="sm"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.PROMPT')"
          @update:model-value="patch({ content: $event })"
        />

        <label
          v-if="type === 'userinput'"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <Switch
            :model-value="node.enabled !== false"
            @update:model-value="
              patch({
                enabled: $event,
                content: $event
                  ? t('AGENT_BOTS.VISUAL_BUILDER.ENABLED')
                  : t('AGENT_BOTS.VISUAL_BUILDER.DISABLED'),
              })
            "
          />
          {{ t('AGENT_BOTS.VISUAL_BUILDER.ENABLED') }}
        </label>

        <div v-if="type === 'oauth'" class="flex flex-col gap-1">
          <span class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.PROVIDER') }}
          </span>
          <Select
            :model-value="node.provider || 'google'"
            :options="OAUTH_PROVIDERS"
            @update:model-value="patch({ provider: $event })"
          />
        </div>

        <CsmlCarouselEditor
          v-if="type === 'carousel'"
          :node="node"
          @patch="patch"
        />
        <CsmlApiEditor v-if="type === 'api'" :node="node" @patch="patch" />
        <CsmlAssignEditor
          v-if="type === 'assign'"
          :node="node"
          @patch="patch"
        />
        <CsmlWorkingHoursEditor
          v-if="type === 'workinghours'"
          :node="node"
          @patch="patch"
        />
      </CsmlPropertySection>

      <CsmlPropertySection
        v-if="hasButtons"
        :title="t('AGENT_BOTS.VISUAL_BUILDER.SECTIONS.BRANCHES')"
      >
        <CsmlOptionListEditor
          v-if="isButtonList || (isStart && node.buttons?.length)"
          :items="node.buttons || []"
          :title="t('AGENT_BOTS.VISUAL_BUILDER.BUTTONS')"
          :add-label="t('AGENT_BOTS.VISUAL_BUILDER.ADD_BUTTON')"
          payload-prefix="btn"
          @update="patch({ buttons: $event })"
        />

        <CsmlOptionListEditor
          v-if="isChoice"
          :items="node.options || []"
          :title="t('AGENT_BOTS.VISUAL_BUILDER.OPTIONS')"
          :add-label="t('AGENT_BOTS.VISUAL_BUILDER.ADD_OPTION')"
          payload-prefix="opt"
          @update="patch({ options: $event })"
        />

        <template v-if="type === 'router'">
          <div
            v-for="(rule, idx) in node.keywords || []"
            :key="`keyword-${idx}`"
            class="flex items-end gap-2"
          >
            <Input
              class="flex-1"
              :model-value="rule.match"
              size="sm"
              :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.KEYWORD')"
              @update:model-value="updateKeyword(idx, $event)"
            />
            <Button
              size="xs"
              variant="ghost"
              color="ruby"
              icon="i-lucide-trash-2"
              is-icon-only
              :title="t('AGENT_BOTS.VISUAL_BUILDER.REMOVE')"
              @click="removeKeyword(idx)"
            />
          </div>
          <Button
            size="sm"
            color="blue"
            variant="faded"
            icon="i-lucide-plus"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.ADD_KEYWORD')"
            @click="addKeyword"
          />
        </template>
      </CsmlPropertySection>

      <CsmlPropertySection
        v-if="hasAdvanced"
        :title="t('AGENT_BOTS.VISUAL_BUILDER.SECTIONS.ADVANCED')"
        :default-open="false"
      >
        <label
          v-if="type === 'url'"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <Switch
            :model-value="!!node.hideUrl"
            @update:model-value="patch({ hideUrl: $event })"
          />
          {{ t('AGENT_BOTS.VISUAL_BUILDER.HIDE_URL') }}
        </label>

        <Input
          v-if="type === 'latex'"
          :model-value="node.tts"
          size="sm"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.TTS')"
          @update:model-value="patch({ tts: $event })"
        />

        <label
          v-if="type === 'question' || (isStart && node.buttons?.length)"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <Switch
            :model-value="node.buttonType === 'quick_reply'"
            @update:model-value="
              patch({ buttonType: $event ? 'quick_reply' : '' })
            "
          />
          {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.QUICK_REPLY') }}
        </label>

        <template v-if="isChoice">
          <Input
            :model-value="node.title"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.TITLE')"
            @update:model-value="patch({ title: $event })"
          />
          <Input
            :model-value="node.description"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.DESCRIPTION')"
            @update:model-value="patch({ description: $event })"
          />
          <Input
            v-if="type === 'dropdown'"
            :model-value="node.placeholder"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.PLACEHOLDER')"
            @update:model-value="patch({ placeholder: $event })"
          />
          <div
            v-if="['radio', 'dropdown'].includes(type)"
            class="flex flex-col gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.PRESELECTED') }}
            </span>
            <Select
              :model-value="node.selected || ''"
              :options="selectedOptions"
              @update:model-value="patch({ selected: $event })"
            />
          </div>
          <Input
            v-if="isMultiSelectLike"
            type="number"
            size="sm"
            :model-value="node.min"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.MIN_SELECTIONS')"
            @update:model-value="patchNumber('min', $event)"
          />
          <Input
            v-if="type !== 'radio'"
            :model-value="node.submit_label"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.SUBMIT_LABEL')"
            @update:model-value="patch({ submit_label: $event })"
          />
        </template>

        <template v-if="type === 'input'">
          <div class="flex flex-col gap-1">
            <span class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.INPUT_TYPE') }}
            </span>
            <Select
              :model-value="node.inputType || 'text'"
              :options="INPUT_TYPES"
              @update:model-value="patch({ inputType: $event })"
            />
          </div>
          <Input
            :model-value="node.title"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.TITLE')"
            @update:model-value="patch({ title: $event })"
          />
          <Input
            :model-value="node.description"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.DESCRIPTION')"
            @update:model-value="patch({ description: $event })"
          />
          <Input
            :model-value="node.placeholder"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.PLACEHOLDER')"
            @update:model-value="patch({ placeholder: $event })"
          />
          <Input
            type="number"
            size="sm"
            :model-value="node.minlength"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.MIN_LENGTH')"
            @update:model-value="patchNumber('minlength', $event)"
          />
          <Input
            type="number"
            size="sm"
            :model-value="node.maxlength"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.MAX_LENGTH')"
            @update:model-value="patchNumber('maxlength', $event)"
          />
          <Input
            :model-value="node.submit_label"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.SUBMIT_LABEL')"
            @update:model-value="patch({ submit_label: $event })"
          />
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <Switch
              :model-value="!!node.required"
              @update:model-value="patch({ required: $event })"
            />
            {{ t('AGENT_BOTS.VISUAL_BUILDER.REQUIRED') }}
          </label>
        </template>

        <template v-if="type === 'calendar'">
          <div class="flex flex-col gap-1">
            <span class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.CALENDAR_MODE') }}
            </span>
            <Select
              :model-value="node.mode || 'single'"
              :options="CALENDAR_MODES"
              @update:model-value="patch({ mode: $event })"
            />
          </div>
          <Input
            type="date"
            size="sm"
            :model-value="node.min_date"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.MIN_DATE')"
            @update:model-value="patch({ min_date: $event })"
          />
          <Input
            type="date"
            size="sm"
            :model-value="node.max_date"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.MAX_DATE')"
            @update:model-value="patch({ max_date: $event })"
          />
        </template>

        <template v-if="type === 'signature'">
          <Input
            :model-value="node.description"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.DESCRIPTION')"
            @update:model-value="patch({ description: $event })"
          />
          <Input
            :model-value="node.submit_label"
            size="sm"
            :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.SUBMIT_LABEL')"
            @update:model-value="patch({ submit_label: $event })"
          />
        </template>
      </CsmlPropertySection>
    </div>

    <footer
      v-if="!isStart"
      class="flex items-center gap-2 px-3 py-2 border-t shrink-0 border-n-weak"
    >
      <Button
        class="flex-1"
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-copy"
        :label="t('AGENT_BOTS.VISUAL_BUILDER.DUPLICATE_NODE')"
        @click="emit('duplicate', node.id)"
      />
    </footer>
  </aside>
</template>
